import { useMemo, useState } from 'react';
import {
  Search, Tag, DollarSign, TrendingDown, TrendingUp, BarChart3, ExternalLink, Landmark,
} from 'lucide-react';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/Table';
import {
  useConsultarPrecos, useBenchmarkCatmat,
  type PrecosFiltros,
} from '@/hooks/useMercadoContratos';
import { Kpi, Painel, Vazio } from './ui';
import { brl2, nInt, dataBR, pncpBusca } from './format';

const curto = (s: string | null, n = 40) => (s && s.length > n ? `${s.slice(0, n)}…` : s || '—');

/** Código de catálogo (CATMAT) mais frequente entre os registros de material. */
function sugerirCatmat(regs: { catalogo_codigo: string | null; material_servico: string | null }[]): string {
  const cont = new Map<string, number>();
  for (const r of regs) {
    const cod = (r.catalogo_codigo || '').trim();
    if (!cod) continue;
    cont.set(cod, (cont.get(cod) ?? 0) + 1);
  }
  let melhor = '';
  let max = 0;
  for (const [cod, n] of cont) {
    if (n > max) { max = n; melhor = cod; }
  }
  return melhor;
}

export function PrecosMercado({ portal }: { portal?: boolean }) {
  const [busca, setBusca] = useState('');
  const [uf, setUf] = useState('');
  const [periodoMeses, setPeriodoMeses] = useState(24);
  const [q, setQ] = useState<PrecosFiltros | null>(null);
  const [codigoFed, setCodigoFed] = useState('');
  const [fedAtivo, setFedAtivo] = useState(false);

  function pesquisar() {
    const termo = busca.trim();
    if (!termo) return;
    const f: PrecosFiltros = { termo };
    if (uf.trim()) f.uf = uf.trim().toUpperCase();
    if (periodoMeses) f.periodoMeses = periodoMeses;
    setQ(f);
    setFedAtivo(false);
    setCodigoFed('');
  }

  const res = useConsultarPrecos(q ?? { termo: '' }, portal);
  const registros = useMemo(() => res.data?.registros ?? [], [res.data?.registros]);
  const stats = res.data?.stats;

  // Sugestão de código CATMAT a partir dos registros (preenche o campo federal).
  const catmatSugerido = useMemo(() => sugerirCatmat(registros), [registros]);
  const codigoEfetivo = (codigoFed || catmatSugerido).trim();
  const bench = useBenchmarkCatmat(
    { codigo: fedAtivo ? codigoEfetivo : '', tipo: 'material' },
    portal
  );
  const b = bench.data;

  const inputCls = 'rounded-md border border-ds-border bg-ds-surface px-2 py-1.5 text-sm text-ds-text-primary';

  return (
    <div className="space-y-5">
      {/* busca */}
      <form
        className="flex flex-wrap items-stretch gap-2"
        onSubmit={(e) => { e.preventDefault(); pesquisar(); }}
      >
        <div className="flex min-w-[260px] flex-[2] items-center gap-2 rounded-md border border-ds-border bg-ds-surface px-3">
          <Search size={16} className="text-ds-text-secondary" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Nome do item (ex.: lente intraocular, cimento, notebook)…"
            className="w-full bg-transparent py-2 text-sm text-ds-text-primary outline-none"
          />
        </div>
        <input value={uf} onChange={(e) => setUf(e.target.value)} placeholder="UF" maxLength={2} className={`${inputCls} w-20`} />
        <select value={periodoMeses} onChange={(e) => setPeriodoMeses(Number(e.target.value))} className={inputCls}>
          {[12, 24, 36, 60].map((m) => <option key={m} value={m}>Últimos {m} meses</option>)}
        </select>
        <button type="submit" className="rounded-md bg-ds-brand-solid px-4 py-2 text-sm font-semibold text-ds-on-brand">
          Buscar preços
        </button>
      </form>

      {!q ? (
        <div className="rounded-lg border border-dashed border-ds-border bg-ds-surface p-8 text-center">
          <Tag size={28} className="mx-auto mb-3 text-ds-text-secondary" />
          <h3 className="text-sm font-semibold text-ds-text-primary">Quanto o governo paga por um item?</h3>
          <p className="mx-auto mt-1 max-w-md text-xs text-ds-text-secondary">
            Pesquise pelo nome do item para ver a faixa de preço praticada (mínimo, mediano e máximo),
            quem comprou e por quanto. Opcionalmente, confira o preço de referência federal por código CATMAT.
          </p>
        </div>
      ) : res.isError ? (
        <Vazio texto="Falha ao consultar preços." />
      ) : (
        <>
          {/* KPIs de preço */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi icon={<BarChart3 size={13} />} titulo="Registros de preço" valor={nInt(stats?.amostras)} detalhe={stats?.editais ? `${nInt(stats.editais)} editais` : undefined} />
            <Kpi icon={<TrendingDown size={13} />} titulo="Menor preço unitário" valor={brl2(stats?.valor_min)} />
            <Kpi icon={<DollarSign size={13} />} titulo="Preço mediano" valor={brl2(stats?.valor_mediana)} detalhe={stats?.valor_medio ? `média ${brl2(stats.valor_medio)}` : undefined} />
            <Kpi icon={<TrendingUp size={13} />} titulo="Maior preço unitário" valor={brl2(stats?.valor_max)} />
          </div>

          {/* benchmark federal (CATMAT) */}
          <Painel titulo="🇧🇷 Preço de referência federal (Compras.gov)">
            <div className="flex flex-wrap items-center gap-2">
              <Landmark size={16} className="text-ds-text-secondary" />
              <input
                value={codigoFed || catmatSugerido}
                onChange={(e) => { setCodigoFed(e.target.value.replace(/\D/g, '')); setFedAtivo(false); }}
                placeholder="Código CATMAT (ex.: 271600)"
                className={`${inputCls} w-48`}
              />
              <button
                type="button"
                onClick={() => setFedAtivo(true)}
                disabled={!codigoEfetivo}
                className="rounded-md border border-ds-border px-3 py-1.5 text-sm font-medium text-ds-text-primary hover:bg-ds-surface2 disabled:opacity-50"
              >
                Consultar federal
              </button>
              <a
                href="https://catalogo.compras.gov.br/cnbs-web/busca"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-ds-brand hover:underline"
              >
                Catálogo CATMAT <ExternalLink size={12} />
              </a>
            </div>

            {catmatSugerido && !codigoFed && (
              <p className="mt-2 text-[11px] text-ds-text-secondary">
                Código sugerido a partir dos registros encontrados: <span className="font-medium text-ds-text-primary">{catmatSugerido}</span>.
              </p>
            )}

            {fedAtivo && (
              bench.isLoading ? <div className="mt-3"><Vazio texto="Consultando Compras.gov…" /></div>
                : !b || b.indisponivel || b.origem_recusou ? (
                  <div className="mt-3 rounded-md border border-ds-border bg-ds-surface2 p-3 text-xs text-ds-text-secondary">
                    {b?.origem_recusou
                      ? 'A fonte federal não respondeu agora. Tente novamente em instantes.'
                      : b?.motivo === 'servico_sem_fonte_federal'
                        ? 'Serviços (CATSER) não têm preço de referência nesta fonte federal.'
                        : 'Sem preço de referência federal para este código.'}
                  </div>
                ) : (
                  <div className="mt-3">
                    {b.descricao && <p className="mb-2 text-xs text-ds-text-secondary">{b.descricao}</p>}
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      <Kpi titulo="Mínimo federal" valor={brl2(b.minimo)} />
                      <Kpi titulo="Mediana federal" valor={brl2(b.mediana)} detalhe={b.media != null ? `média ${brl2(b.media)}` : undefined} />
                      <Kpi titulo="Máximo federal" valor={brl2(b.maximo)} />
                      <Kpi titulo="Amostras federais" valor={nInt(b.amostras)} detalhe={b.periodo_inicio ? `desde ${dataBR(b.periodo_inicio)}` : undefined} />
                    </div>
                  </div>
                )
            )}
          </Painel>

          {/* tabela de registros */}
          <Painel titulo="📋 Registros de preço encontrados">
            {res.isLoading ? <Vazio texto="Carregando…" />
              : registros.length === 0 ? <Vazio texto="Nenhum registro de preço para esse item no recorte." />
                : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead>Órgão</TableHead>
                        <TableHead>Local</TableHead>
                        <TableHead className="text-right">Qtd.</TableHead>
                        <TableHead className="text-right">Vlr. unitário</TableHead>
                        <TableHead className="text-right">Data</TableHead>
                        <TableHead className="text-right">PNCP</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {registros.map((r, i) => (
                        <TableRow key={`${r.numero_controle_pncp ?? 'sem'}-${i}`}>
                          <TableCell className="max-w-[260px]">
                            <div className="truncate text-ds-text-primary" title={r.descricao || ''}>{curto(r.descricao, 48)}</div>
                            {r.catalogo_codigo && <div className="text-[11px] text-ds-text-secondary">CATMAT {r.catalogo_codigo}</div>}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate" title={r.orgao || ''}>{r.orgao || '—'}</TableCell>
                          <TableCell className="whitespace-nowrap">{[r.municipio, r.uf].filter(Boolean).join('/') || '—'}</TableCell>
                          <TableCell className="text-right tabular-nums">{nInt(r.quantidade)}</TableCell>
                          <TableCell className="text-right tabular-nums text-ds-success">{brl2(r.valor_unitario_estimado)}</TableCell>
                          <TableCell className="text-right tabular-nums">{dataBR(r.data)}</TableCell>
                          <TableCell className="text-right">
                            {r.numero_controle_pncp ? (
                              <a href={pncpBusca(r.numero_controle_pncp)} target="_blank" rel="noreferrer" className="inline-flex text-ds-brand hover:underline" title="Abrir no PNCP">
                                <ExternalLink size={15} />
                              </a>
                            ) : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
          </Painel>
        </>
      )}
    </div>
  );
}

export default PrecosMercado;
